'use client'

import React from 'react'

import { Inter, Jost } from 'next/font/google'
import { Ghost } from 'lucide-react'

import { cn } from '@rewardops/ui'
import { useApi } from '~/components/hooks/use-api'
import { useTranslation } from '~/app/i18n/client'

const fontSans = Inter({
  subsets: ['latin'],
  variable: '--font-sans',
})

const jost = Jost({ subsets: ['latin'] })

interface NotFoundProps {
  page?: 'default' | 'promotions'
}

const NotFound: React.FC<NotFoundProps> = ({ page = 'default' }) => {
  const { tenant } = useApi()
  const { t } = useTranslation()

  const translationKey = `notFound.${page}`
  const description = t(`${translationKey}.description`, { defaultValue: t('notFound.description') })
  const mapleDescription = t(`${translationKey}.mapleDescription`, { defaultValue: t('notFound.mapleDescription') })
  const redirectToText = t(`${translationKey}.redirectToText`, { defaultValue: t('notFound.goHome') })
  const redirectToLink = t(`${translationKey}.redirectToLink`, { defaultValue: '/' })

  if (tenant?.theme === 'maple' || tenant?.theme === 'pointsAtWork') {
    return (
      <div className={cn('h-screen', jost.className)}>
        <main className="grid min-h-full place-items-center bg-white px-6 py-24 sm:py-8 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center pb-4"><Ghost size={64} /></div>
            <h1 className="mt-4 text-3xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
              {t('notFound.mapleHeader')}
            </h1>
            <div>
              <p className="mt-6 text-base leading-7 text-gray-600">{mapleDescription}</p>
              <div className="flex items-center justify-center gap-x-6 pt-6">
                <a
                  href={redirectToLink}
                  className="mt-6 w-4/5 rounded-full bg-black px-6 py-4 text-lg font-semibold text-white shadow-sm hover:bg-gray-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
                >
                  {redirectToText}
                </a>
              </div>
            </div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className={cn('h-screen font-sans', fontSans.variable)}>
      <main className="grid min-h-full place-items-center bg-white px-6 py-24 sm:py-32 lg:px-8">
        <div className="text-center">
          <p className="text-base font-semibold text-indigo-600">404</p>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            {t('notFound.header')}
          </h1>
          <p className="mt-6 text-base leading-7 text-gray-600">{description}</p>
          <div className="mt-10 flex items-center justify-center gap-x-6">
            <a
              href={redirectToLink}
              className="rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              {redirectToText}
            </a>
          </div>
        </div>
      </main>
    </div>
  )
}

export default NotFound
